import DashboardService from "@/src/api/DashboardService.js";
import { prStatuses } from "@/src/constants/codebaseConstants.js";
import { entityTypes } from "@/src/constants/entityTypeConstants.js";
import { fetchLifecycleStageOptions } from "./companyConstants.js";
import { signalStrengthOptions, signalTypes } from "./signalsConstants";
import { featureNames } from "@/src/constants/globalConstants.js";
import dayjs from "dayjs";

export const priorityOptions = [
  { id: "High", name: "High" },
  { id: "Medium", name: "Medium" },
  { id: "Low", name: "Low" },
];

export const dealTypeOptions = [
  { id: "New", name: "New" },
  { id: "Existing", name: "Existing" },
];

export const commonDateOptions = [
  { id: "today", name: "Today" },
  { id: "yesterday", name: "Yesterday" },
  { id: "last_3_days", name: "Last 3 days" },
  { id: "last_7_days", name: "Last 7 days" },
  { id: "last_30_days", name: "Last 30 days" },
  { id: "custom", name: "Custom" },
];

export const closeDateOptions = [
  { id: "today", name: "Today" },
  { id: "yesterday", name: "Yesterday" },
  { id: "tomorrow", name: "Tomorrow" },
  { id: "this_week", name: "This Week" },
  { id: "this_week_so_far", name: "This Week so far" },
  { id: "last_week", name: "Last Week" },
  { id: "next_week", name: "Next Week" },
  { id: "this_month", name: "This Month" },
  { id: "this_month_so_far", name: "This Month so far" },
  { id: "this_quarter", name: "This Quarter" },
  { id: "this_quarter_so_far", name: "This Quarter so far" },
  { id: "last_month", name: "Last Month" },
  { id: "next_month", name: "Next Month" },
  { id: "last_quarter", name: "Last Quarter" },
  { id: "next_quarter", name: "Next Quarter" },
  { id: "this_year", name: "This Year" },
  { id: "this_year_so_far", name: "This Year so far" },
  { id: "last_year", name: "Last Year" },
  { id: "next_year", name: "Next Year" },
  { id: "last_7_days", name: "Last 7 days" },
  { id: "last_30_days", name: "Last 30 days" },
  { id: "custom", name: "Custom" },
];

const dateRangeConfig = {
  today: { start: { unit: "day" }, end: { unit: "day" } },
  yesterday: {
    start: { offset: -1, unit: "day" },
    end: { offset: -1, unit: "day" },
  },
  tomorrow: {
    start: { offset: 1, unit: "day" },
    end: { offset: 1, unit: "day" },
  },
  last_3_days: {
    start: { offset: -2, unit: "day", noEnd: true },
    end: { current: true },
  },
  last_7_days: {
    start: { offset: -6, unit: "day", noEnd: true },
    end: { current: true },
  },
  last_30_days: {
    start: { offset: -29, unit: "day", noEnd: true },
    end: { current: true },
  },
  this_week: { start: { unit: "week" }, end: { unit: "week" } },
  this_week_so_far: {
    start: { unit: "week", noEnd: true },
    end: { current: true },
  },
  last_week: {
    start: { offset: -1, unit: "week" },
    end: { offset: -1, unit: "week" },
  },
  next_week: {
    start: { offset: 1, unit: "week" },
    end: { offset: 1, unit: "week" },
  },
  this_month: { start: { unit: "month" }, end: { unit: "month" } },
  this_month_so_far: {
    start: { unit: "month", noEnd: true },
    end: { current: true },
  },
  last_month: {
    start: { offset: -1, unit: "month" },
    end: { offset: -1, unit: "month" },
  },
  next_month: {
    start: { offset: 1, unit: "month" },
    end: { offset: 1, unit: "month" },
  },
  this_quarter: { start: { unit: "quarter" }, end: { unit: "quarter" } },
  this_quarter_so_far: {
    start: { unit: "quarter", noEnd: true },
    end: { current: true },
  },
  last_quarter: {
    start: { offset: -1, unit: "quarter" },
    end: { offset: -1, unit: "quarter" },
  },
  next_quarter: {
    start: { offset: 1, unit: "quarter" },
    end: { offset: 1, unit: "quarter" },
  },
  this_year: { start: { unit: "year" }, end: { unit: "year" } },
  this_year_so_far: {
    start: { unit: "year", noEnd: true },
    end: { current: true },
  },
  last_year: {
    start: { offset: -1, unit: "year" },
    end: { offset: -1, unit: "year" },
  },
  next_year: {
    start: { offset: 1, unit: "year" },
    end: { offset: 1, unit: "year" },
  },
};

const calculateDate = (now, config) => {
  if (config.current) return now.endOf("day");

  let date = now;
  if (config.offset) {
    date = date.add(config.offset, config.unit);
  }

  if (config.noEnd) {
    return date.startOf(config.unit);
  }

  return date.startOf(config.unit);
};

const calculateEndDate = (now, config) => {
  if (config.current) return now.endOf("day");

  let date = now;
  if (config.offset) {
    date = date.add(config.offset, config.unit);
  }

  return date.endOf(config.unit);
};

export const calculateDateRange = (option) => {
  const now = dayjs();
  const config = dateRangeConfig[option];

  if (!config) {
    return {
      start: now.startOf("day").format("YYYY-MM-DD"),
      end: now.endOf("day").format("YYYY-MM-DD"),
    };
  }

  const start = calculateDate(now, config.start);
  const end = calculateEndDate(now, config.end);

  return {
    start: start.format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD"),
  };
};

const createDateFilter = (id, name) => ({
  id,
  name,
  options: commonDateOptions,
  searchable: false,
  multiSelect: false,
  showInitials: false,
});

const createTagFilter = (id = "tags", name = "Tags") => ({
  id,
  name,
  options: [], // This will be populated dynamically
  searchable: true,
  multiSelect: true,
  showInitials: false,
});

export const companyFilterKeys = {
  OWNER: "owner",
  INDUSTRY: "industry",
  COMPANY_SIZE: "company_size",
  TAGS: "tags",
  CREATED_DATE: "created_date",
  LAST_ACTIVITY: "last_activity",
  LIFECYCLE_STAGE: "lifecycle_stage",
};

export const companySizeOptions = [
  { id: "1-10", name: "1-10" },
  { id: "11-50", name: "11-50" },
  { id: "51-100", name: "51-100" },
  { id: "101-500", name: "101-500" },
  { id: "501-1000", name: "501-1000" },
  { id: "1001-5000", name: "1001-5000" },
  { id: "5001-10,000", name: "5001-10,000" },
  { id: "10,000+", name: "10,000+" },
];

export const companyFilters = [
  {
    id: companyFilterKeys.OWNER,
    name: "Company Owner",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: companyFilterKeys.COMPANY_SIZE,
    name: "Company Size",
    options: companySizeOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: companyFilterKeys.INDUSTRY,
    name: "Industry",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  // {
  //   id: companyFilterKeys.LIFECYCLE_STAGE,
  //   name: "Lifecycle Stage",
  //   options: [],
  //   searchable: false,
  //   multiSelect: false,
  //   showInitials: false,
  // },
  createTagFilter(companyFilterKeys.TAGS),
  createDateFilter(companyFilterKeys.CREATED_DATE, "Created Date"),
  createDateFilter(companyFilterKeys.LAST_ACTIVITY, "Last Activity"),
];

// Function to update lifecycle stage options in company filters
export const updateCompanyLifecycleStageOptions = async () => {
  try {
    const options = await fetchLifecycleStageOptions();
    const lifecycleStageFilter = companyFilters.find(
      (filter) => filter.id === companyFilterKeys.LIFECYCLE_STAGE,
    );
    if (lifecycleStageFilter) {
      lifecycleStageFilter.options = options.map((option) => ({
        id: option.value, // Use the value for filtering
        name: option.label, // Use the label for display
      }));
    }
  } catch (error) {
    console.error("Error updating lifecycle stage options:", error);
  }
};

export const contactFilters = [
  {
    id: "company",
    name: "Company",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "owner",
    name: "Contact Owner",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "contact_status",
    name: "Contact Status",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  createTagFilter(),
  createDateFilter("created_date", "Created Date"),
  createDateFilter("last_activity", "Last Activity"),
];

export const leadFilters = [
  {
    id: "company",
    name: "Company",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "owner",
    name: "Lead Owner",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
    sortSelectedFirst: true,
  },
  {
    id: "lead_status",
    name: "Lead Status",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "sources",
    name: "Lead Source",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  createTagFilter(),
  createDateFilter("created_date", "Created Date"),
  createDateFilter("last_activity", "Last Activity"),
];

export const dealFilters = [
  {
    id: "deal_company",
    name: "Deal Company",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "deal_owner",
    name: "Deal Owner",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "deal_stage",
    name: "Deal Stage",
    options: [
      { id: "deal_1", name: "Deal Status 1" },
      { id: "deal_2", name: "Deal Status 2" },
      { id: "deal_3", name: "Deal Status 3" },
      { id: "deal_1", name: "Deal Status 4" },
      { id: "deal_1", name: "Deal Status 5" },
    ],
    searchable: true,
    multiSelect: true,
    showInitials: false,
    customWidth: "w-fit",
  },
  {
    id: "priority",
    name: "Priority",
    options: priorityOptions,
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  createTagFilter(),
  {
    id: "close_date",
    name: "Expected Close Date",
    options: closeDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  createDateFilter("created_date", "Created Date"),
  createDateFilter("last_activity", "Last Activity"),
];

export const actionFilters = [
  {
    id: "assigned_to",
    name: "Assigned To",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "priority",
    name: "Priority",
    options: priorityOptions,
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "status",
    name: "Status",
    options: [
      { id: "Todo", name: "Todo" },
      { id: "Done", name: "Done" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "due_date",
    name: "Due Date",
    options: [
      { id: "today", name: "Today" },
      { id: "tomorrow", name: "Tomorrow" },
      { id: "yesterday", name: "Yesterday" },
      { id: "last_3_days", name: "Last 3 days" },
      { id: "next_3_days", name: "Next 3 days" },
      { id: "last_7_days", name: "Last 7 days" },
      { id: "next_7_days", name: "Next 7 days" },
      { id: "last_30_days", name: "Last 30 days" },
      { id: "next_30_days", name: "Next 30 days" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  createTagFilter(),
  createDateFilter("created_date", "Created Date"),
];

export const taskFilterKeys = {
  STATUS: "status",
  ASSIGNED_TO: "assigned_to",
  ASSIGNED_TO_USER: "assigned_to_user",
  ASSIGNED_TO_TEAM: "assigned_to_team",
  PRIORITY: "priority",
  DUE_DATE: "due_date",
  ACTION_TYPE: "action_type",
  CREATED_DATE: "created_date",
  LAST_ACTIVITY: "last_activity",
};

export const taskFilters = [
  {
    id: taskFilterKeys.STATUS,
    name: "Status",
    options: [],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: taskFilterKeys.ASSIGNED_TO,
    name: "Assignee",
    withTabs: true,
    tabs: [
      {
        id: taskFilterKeys.ASSIGNED_TO_USER,
        name: "Users",
        options: [],
        searchable: true,
        multiSelect: true,
        showInitials: true,
      },
      {
        id: taskFilterKeys.ASSIGNED_TO_TEAM,
        name: "Teams",
        options: [],
        searchable: true,
        multiSelect: true,
        showInitials: false,
      },
    ],
  },
  {
    id: taskFilterKeys.PRIORITY,
    name: "Priority",
    options: priorityOptions,
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: taskFilterKeys.ACTION_TYPE,
    name: "Type",
    options: [],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: taskFilterKeys.DUE_DATE,
    name: "Due Date",
    options: [
      { id: "today", name: "Today" },
      { id: "tomorrow", name: "Tomorrow" },
      { id: "yesterday", name: "Yesterday" },
      { id: "last_3_days", name: "Last 3 days" },
      { id: "next_3_days", name: "Next 3 days" },
      { id: "last_7_days", name: "Last 7 days" },
      { id: "next_7_days", name: "Next 7 days" },
      { id: "last_30_days", name: "Last 30 days" },
      { id: "next_30_days", name: "Next 30 days" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  createDateFilter(taskFilterKeys.CREATED_DATE, "Created On"),
  createDateFilter(taskFilterKeys.LAST_ACTIVITY, "Last Activity"),
];

export const listFilters = [
  {
    id: "object",
    name: "Object",
    options: ["Contacts", "Companies"],
  },
  {
    id: "created_by",
    name: "Contact By",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  createDateFilter("last_activity", "Last Activity"),
];

export const campaignRepliesFilters = [
  {
    id: "campaign_ids",
    name: "Campaign",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "campaign_categories",
    name: "Category",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
];

export const formFilters = [
  {
    id: "statuses",
    name: "Status",
    options: [
      { id: "ACTIVE", name: "Active" },
      { id: "DRAFT", name: "Draft" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "created_date",
    name: "Created Date",
    options: [
      { id: "today", name: "Today" },
      { id: "this_week", name: "This Week" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "updated_date",
    name: "Last Updated",
    options: [
      { id: "today", name: "Today" },
      { id: "this_week", name: "This Week" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const campaignFilters = [
  {
    id: "statuses",
    name: "Status",
    options: [
      { id: "DRAFT", name: "Drafts" },
      { id: "IN_PROGRESS", name: "In Progress" },
      { id: "PAUSED", name: "Paused" },
      { id: "COMPLETE", name: "Completed" },
      { id: "ENDED", name: "Stopped" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "created_date",
    name: "Created Date",
    options: [
      { id: "today", name: "Today" },
      { id: "this_week", name: "This Week" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const sequenceFilters = [
  {
    id: "statuses",
    name: "Status",
    options: [
      { id: "ACTIVE", name: "Active" },
      { id: "IN_ACTIVE", name: "Inactive" },
      { id: "COMPLETE", name: "Complete" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "created_date",
    name: "Created Date",
    options: [
      { id: "today", name: "Today" },
      { id: "this_week", name: "This Week" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  createTagFilter(),
];

export const sequenceViewFilters = [
  {
    id: "sequence_step",
    name: `${featureNames.SEQUENCES} Step`,
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "status",
    name: "Status",
    options: [
      { id: "", name: "All" },
      { id: "IN_PROGRESS", name: "Active" },
      { id: "PAUSED", name: "Paused" },
      { id: "COMPLETE", name: "Complete" },
      { id: "REPLIED", name: "Replied" },
      { id: "NOT_SENT", name: "Not Sent" },
      { id: "BOUNCED", name: "Bounced" },
      { id: "UNSUBSCRIBED", name: "Unsubscribed" },
      { id: "REMOVED", name: "Removed" },
      { id: "UNQUALIFIED", name: "Unqualified" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const entityListFilters = [
  {
    id: "object",
    name: "Object",
    options: [
      { id: "contact", name: "Contact" },
      { id: "company", name: "Company" },
      { id: "lead", name: "Lead" },
    ],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "created_by",
    name: "Created By",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "privacy",
    name: "Privacy",
    options: [
      { id: "0", name: "Public" },
      { id: "1", name: "Private" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  createDateFilter("created_date", "Created Date"),
  createDateFilter("last_activity", "Last Activity"),
];

export const emailFilters = [
  {
    id: "sequences",
    name: featureNames.SEQUENCES,
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "list",
    name: "List",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "senders_email",
    name: "Senders Email",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  createDateFilter("sent_on", "Sent On"),
  {
    id: "email_stage",
    name: "Email Stage",
    options: [
      { id: "DELIVERED", name: "Delivered" },
      { id: "OPENED", name: "Opened" },
      { id: "CLICKED", name: "Clicked" },
      { id: "REPLIED", name: "Replied" },
      { id: "BOUNCED", name: "Bounced" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "minimum_opened_count",
    name: "Opened",
    options: [],
    searchable: false,
    multiSelect: false,
    showInitials: false,
    customWidth: "w-80",
  },
  {
    id: "campaign",
    name: "Campaign",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
];

export const linkedInLogFilters = [
  {
    id: "sequence_id",
    name: featureNames.SEQUENCES,
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "campaign_id",
    name: "Campaign",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "linkedin_integration_id",
    name: "Sender Account",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  createDateFilter("time_filter", "Sent On"),
  {
    id: "connection_request",
    name: "Connection Request",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "ACCEPTED", name: "Accepted" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "message",
    name: "Message",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "REPLIED", name: "Replied" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "inmail",
    name: "InMail",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "REPLIED", name: "Replied" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
];

export const sequenceEmailFilters = [
  {
    id: "senders_email",
    name: "Senders Email",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "sent_on",
    name: "Sent On",
    options: [
      { id: "today", name: "Today" },
      { id: "yesterday", name: "Yesterday" },
      { id: "last_3_days", name: "Last 3 days" },
      { id: "last_7_days", name: "Last 7 days" },
      { id: "last_30_days", name: "Last 30 days" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "email_stage",
    name: "Email Stage",
    options: [
      { id: "DELIVERED", name: "Delivered" },
      { id: "OPENED", name: "Opened" },
      { id: "CLICKED", name: "Clicked" },
      { id: "REPLIED", name: "Replied" },
      { id: "BOUNCED", name: "Bounced" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "minimum_opened_count",
    name: "Opened",
    options: [],
    searchable: false,
    multiSelect: false,
    showInitials: false,
    customWidth: "w-80",
  },
];

export const sequenceLinkedInLogFilters = [
  {
    id: "linkedin_integration_id",
    name: "Sender Account",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  createDateFilter("time_filter", "Sent On"),
  {
    id: "connection_request",
    name: "Connection Request",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "ACCEPTED", name: "Accepted" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "message",
    name: "Message",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "REPLIED", name: "Replied" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "inmail",
    name: "InMail",
    options: [
      { id: "SENT", name: "Sent" },
      { id: "REPLIED", name: "Replied" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
];

export const sequenceAnalyticsFilters = [
  createDateFilter("created_date", "Time Range"),
];

const commonDateFilters = [
  { id: "today", name: "Today" },
  { id: "this_week", name: "This Week" },
  { id: "this_month", name: "This Month" },
  { id: "all_time", name: "All Time" },
  { id: "custom", name: "Custom" },
];

export const createDateFilters = (id, name) => ({
  id,
  name,
  options: commonDateFilters,
  searchable: false,
  multiSelect: false,
  showInitials: false,
});

export const meetingLogsFilters = [
  createDateFilters("date", "Date"),
  {
    id: "result_types",
    name: "Status",
    options: [
      { id: "upcoming", name: "Upcoming" },
      { id: "cancelled", name: "Cancelled" },
      { id: "past", name: "Past" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "meeting_agent",
    name: "Meeting Agent",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "host_emails",
    name: "Host Email",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "tags",
    name: "Tags",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "disposition",
    name: "Disposition",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "meeting_link",
    name: "Meeting Link",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: false,
    allowCustomInput: true,
  },
  {
    id: "ai_notetaker",
    name: "AI Notetaker",
    options: [
      { id: "recorded", name: "Meeting Recorded" },
      { id: "not_recorded", name: "Meeting Not Recorded" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "is_synced_from_calendar",
    name: "Synced From Calendar",
    options: [
      { id: "yes", name: "Yes" },
      { id: "no", name: "No" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const campaignInEngageFilters = [
  {
    id: "channel",
    name: "Channel",
    options: [
      { id: "EMAIL", name: "Email" },
      { id: "WHATSAPP", name: "WhatsApp" },
      { id: "SMS", name: "SMS" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "status",
    name: "Status",
    options: [
      { id: "all", name: "All" },
      { id: "sent", name: "Sent" },
      { id: "scheduled", name: "Scheduled" },
      { id: "draft", name: "Drafts" },
      { id: "in_progress", name: "In Progress" },
      { id: "failed", name: "Failed" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  // {
  //   id: "recipients",
  //   name: "Recipients",
  //   options: [],
  //   searchable: true,
  //   multiSelect: false,
  //   showInitials: false,
  // },
  createDateFilters("date", "Created Date"),
];

export const responseLogsFilters = [
  createDateFilters("time_range", "Time Range"),
  {
    id: "result_types",
    name: "Routed To",
    options: [
      { id: "event_scheduled", name: "Event Scheduled" },
      { id: "no_event_scheduled", name: "No Event Scheduled" },
      { id: "external_redirect", name: "External Redirect" },
      { id: "custom_message", name: "Custom Message" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "meeting_agent",
    name: "Meeting Agent",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: false,
  },
];

export const enrichDataFilters = [
  {
    id: "status",
    name: "Status",
    options: [
      { id: "pending", name: "Pending" },
      { id: "processing", name: "Processing" },
      { id: "completed", name: "Completed" },
      { id: "failed", name: "Failed" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "entity",
    name: "Entity",
    options: [
      { id: "person", name: "People" },
      { id: "company", name: "Companies" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "created_by",
    name: "Created By",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
];

export const signalStreamFilters = [
  createDateFilter("date", "Last Activity"),
  {
    id: "strength",
    name: "Strength",
    options: signalStrengthOptions,
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "type",
    name: "Type",
    options: signalTypes
      .filter((type) => !type.hidden)
      .map((type) => ({ id: type.key, name: type.label })),
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "signal_ids",
    name: "Signal",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
  },
];

export const prospectFilterKeys = {
  JOB_TITLE: "job_title",
  DEPARTMENT: "department",
  SENIORITY: "seniority",
  LOCATION: "lead_location",
};

export const prospectFilterTypes = Object.values(prospectFilterKeys);

export const PROSPECT_FILTER_DEFAULT_DISPLAY_COUNT = 20;
export const PROSPECT_FILTER_PAGE_SIZE = 20;

export const prospectFilters = [
  {
    id: prospectFilterKeys.JOB_TITLE,
    name: "Job Title",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
    customWidth: "w-64",
  },
  {
    id: prospectFilterKeys.DEPARTMENT,
    name: "Department",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
    customWidth: "w-64",
  },
  {
    id: prospectFilterKeys.SENIORITY,
    name: "Seniority",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
    customWidth: "w-64",
  },
  {
    id: prospectFilterKeys.LOCATION,
    name: "Location",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: false,
    customWidth: "w-64",
  },
];

export const exportEmailNotRequired = [entityTypes.MEETING_LOGS];
export const exportEmailNotRequiredFunctions = {
  [entityTypes.MEETING_LOGS]: DashboardService.exportMeetingLogsView,
};

export const COMPANY_KEYWORD_FIELDS = [
  "company_keywords",
  "and_company_keywords",
  "exclude_company_keywords",
  "industry_keywords",
  "exclude_industry_keywords",
];
export const LEAD_KEYWORD_FIELDS = [
  "keywords",
  "and_keywords",
  "exclude_keywords",
  "company_keywords",
  "and_company_keywords",
  "exclude_company_keywords",
];

export const appSubmissionFilters = [
  {
    id: "statuses",
    name: "Status",
    options: [
      { id: "under_review", name: "Under Review" },
      { id: "approved", name: "Approved" },
      { id: "rejected", name: "Rejected" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "types",
    name: "Type",
    options: [
      { id: "app", name: "App" },
      { id: "digital_employee", name: "Digital Employee" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "submitted_date",
    name: "Submitted date",
    options: [
      { id: "today", name: "Today" },
      { id: "this_week", name: "This Week" },
      { id: "this_month", name: "This Month" },
      { id: "last_month", name: "Last Month" },
      { id: "custom", name: "Custom" },
    ],
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const pullRequestsFilters = [
  {
    id: "status",
    name: "Status",
    options: [
      { id: prStatuses.OPEN, name: "Open" },
      { id: prStatuses.MERGED, name: "Merged" },
      { id: prStatuses.CLOSED, name: "Closed" },
      { id: prStatuses.DRAFT, name: "Draft" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "contributor",
    name: "Contributor",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
];

export const commitsFilters = [
  {
    id: "author",
    name: "Users",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "date",
    name: "Date",
    options: commonDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const prActivityFilterKeys = {
  TYPE: "type",
  AUTHOR: "author",
  DATE: "date",
};

export const prActivityFilters = [
  {
    id: prActivityFilterKeys.TYPE,
    name: "Activity type",
    options: [
      { id: "commit", name: "Commit" },
      { id: "comment", name: "Comment" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: prActivityFilterKeys.AUTHOR,
    name: "Updated by",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: prActivityFilterKeys.DATE,
    name: "Updated date",
    options: commonDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];

export const actionsRunFilters = [
  {
    id: "event",
    name: "Event",
    options: [
      { id: "push", name: "Push" },
      { id: "pull_request", name: "Pull Request" },
      { id: "workflow_dispatch", name: "Manual" },
      { id: "schedule", name: "Schedule" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "assignee",
    name: "Assignee",
    options: [],
    searchable: true,
    multiSelect: true,
    showInitials: true,
  },
  {
    id: "branch",
    name: "Branch",
    options: [],
    searchable: true,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "status",
    name: "Status",
    options: [
      { id: "completed", name: "Success" },
      { id: "failed", name: "Failed" },
      { id: "in_progress", name: "Running" },
      { id: "queued", name: "Queued" },
      { id: "cancelled", name: "Cancelled" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
];

export const sandboxFilters = [
  {
    id: "status",
    name: "Status",
    options: [
      { id: "Creating", name: "Creating" },
      { id: "Running", name: "Running" },
      { id: "Pending", name: "Pending" },
      { id: "Resuming", name: "Resuming" },
      { id: "Pausing", name: "Pausing" },
      { id: "Paused", name: "Paused" },
      { id: "Archiving", name: "Archiving" },
      { id: "Archived", name: "Archived" },
    ],
    searchable: false,
    multiSelect: true,
    showInitials: false,
  },
  {
    id: "created_date",
    name: "Created Date",
    options: commonDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
  {
    id: "last_activity",
    name: "Last Activity",
    options: commonDateOptions,
    searchable: false,
    multiSelect: false,
    showInitials: false,
  },
];